import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  ArrowLeft, UtensilsCrossed, Target, Flame, Heart, ExternalLink,
  SlidersHorizontal, Store, Bookmark, Plus, X, Lock, Info, Tag
} from 'lucide-react';
import HamburgerMenu from '../components/HamburgerMenu';
import { API, getLocalDateString } from '../App';

const DIET_PREFS = ['none', 'vegetarian', 'vegan', 'pescatarian', 'keto', 'gluten_free', 'dairy_free'];
const ALLERGIES = ['dairy', 'gluten', 'nuts', 'peanuts', 'soy', 'eggs', 'fish', 'shellfish', 'sesame'];
const CATEGORIES = ['breakfast', 'lunch', 'dinner', 'snack', 'high_protein', 'post_workout'];

const tagLabel = (t) => (t || '').replace(/_/g, ' ');

const MatchBadge = ({ label }) => {
  const styles = {
    'Excellent Match': 'bg-green-900/60 text-green-300 border-green-700',
    'Good Match': 'bg-amber-900/60 text-amber-300 border-amber-700',
    'Fair Match': 'bg-stone-800 text-stone-400 border-stone-600',
  };
  return (
    <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${styles[label] || styles['Fair Match']}`}>
      {label}
    </span>
  );
};

const MealCard = ({ meal, onDetails, onOrder, onSave, isSaved }) => (
  <Card className="glass border border-stone-800 overflow-hidden flex flex-col hover:border-blood-800 transition-colors" data-testid={`meal-card-${meal.id}`}>
    <div className="relative h-40 bg-stone-900">
      {meal.image_url && (
        <img src={meal.image_url} alt={meal.name} className="w-full h-full object-cover" loading="lazy" />
      )}
      <div className="absolute top-2 left-2">{meal.match_label && <MatchBadge label={meal.match_label} />}</div>
      <button
        onClick={() => onSave(meal)}
        className="absolute top-2 right-2 bg-stone-950/70 rounded-full p-2 hover:bg-stone-900"
        data-testid={`meal-save-btn-${meal.id}`}
      >
        <Heart className={`w-4 h-4 ${isSaved ? 'fill-blood-500 text-blood-500' : 'text-stone-300'}`} />
      </button>
      {meal.price != null && (
        <span className="absolute bottom-2 right-2 bg-stone-950/80 text-white text-sm font-bold px-2 py-0.5 rounded">
          ${meal.price.toFixed(2)}
        </span>
      )}
    </div>
    <div className="p-4 flex-1 flex flex-col">
      <p className="text-stone-500 text-xs uppercase tracking-wider">{meal.provider_name}</p>
      <h3 className="text-white font-bold leading-snug mb-2">{meal.name}</h3>
      <div className="grid grid-cols-4 gap-1 text-center mb-2">
        {[['Cal', meal.calories], ['P', `${meal.protein}g`], ['C', `${meal.carbs}g`], ['F', `${meal.fats}g`]].map(([k, v]) => (
          <div key={k} className="bg-stone-900/70 rounded py-1">
            <p className="text-blood-400 text-xs font-bold">{v}</p>
            <p className="text-stone-500 text-[10px]">{k}</p>
          </div>
        ))}
      </div>
      {meal.dietary_tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {meal.dietary_tags.slice(0, 3).map(t => (
            <span key={t} className="text-[10px] bg-stone-800 text-stone-400 px-1.5 py-0.5 rounded capitalize">{tagLabel(t)}</span>
          ))}
        </div>
      )}
      {meal.match_reasons?.[0] && (
        <p className="text-stone-400 text-xs italic mb-3 flex-1">{meal.match_reasons[0]}</p>
      )}
      <div className="flex gap-2 mt-auto">
        <Button size="sm" variant="outline" onClick={() => onDetails(meal)}
          className="flex-1 glass border-stone-700 text-stone-300 hover:bg-stone-800 text-xs"
          data-testid={`meal-details-btn-${meal.id}`}>
          View Details
        </Button>
        <Button size="sm" onClick={() => onOrder(meal, 'meal_card')}
          className="flex-1 bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 text-white text-xs font-bold"
          data-testid={`meal-order-btn-${meal.id}`}>
          Order <ExternalLink className="w-3 h-3 ml-1" />
        </Button>
      </div>
    </div>
  </Card>
);

export default function PreparedMeals({ user }) {
  const navigate = useNavigate();
  const isPremium = user && user.tier === 'premium';
  const [tab, setTab] = useState('goal'); // goal | today | saved | providers
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recs, setRecs] = useState(null);
  const [savedMeals, setSavedMeals] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [providers, setProviders] = useState([]);
  const [prefs, setPrefs] = useState({ dietary_preference: 'none', allergies: [], meals_per_day: 3 });
  const [showPrefs, setShowPrefs] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ provider_id: '', max_calories: '', min_protein: '', max_carbs: '', max_fats: '', max_price: '', dietary_tag: '', category: '' });
  const [sort, setSort] = useState('best');
  const [detail, setDetail] = useState(null);
  const [logForm, setLogForm] = useState(null);

  const loadRecs = useCallback(async (mode) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/meals/recommendations?mode=${mode}&date=${getLocalDateString()}`);
      setRecs(res.data);
      setPrefs(p => ({ ...p, ...res.data.preferences }));
    } catch (e) {
      setError(e.response?.status === 403 ? 'premium' : 'Failed to load recommendations.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSaved = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/meals/saved`);
      setSavedMeals(res.data.meals || []);
      setSavedIds(new Set((res.data.meals || []).map(m => m.id)));
    } catch (e) { /* non-fatal */ }
  }, []);

  const loadProviders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/meals/providers`);
      setProviders(res.data.providers || []);
    } catch (e) { /* non-fatal */ }
  }, []);

  useEffect(() => {
    if (!isPremium) { setLoading(false); return; }
    loadRecs('goal');
    loadSaved();
    loadProviders();
    axios.get(`${API}/meals/preferences`).then(r => setPrefs(r.data)).catch(() => {});
  }, [isPremium, loadRecs, loadSaved, loadProviders]);

  const switchTab = (t) => {
    setTab(t);
    if (t === 'goal' || t === 'today') loadRecs(t);
    if (t === 'saved') loadSaved();
  };

  const savePrefs = async (next) => {
    setPrefs(next);
    try {
      await axios.put(`${API}/meals/preferences`, {
        dietary_preference: next.dietary_preference,
        allergies: next.allergies,
        meals_per_day: next.meals_per_day,
      });
      if (tab === 'goal' || tab === 'today') loadRecs(tab);
      toast.success('Preferences updated');
    } catch (e) {
      toast.error('Failed to save preferences');
    }
  };

  const toggleAllergy = (a) => {
    const has = prefs.allergies?.includes(a);
    savePrefs({ ...prefs, allergies: has ? prefs.allergies.filter(x => x !== a) : [...(prefs.allergies || []), a] });
  };

  const handleSave = async (meal) => {
    try {
      if (savedIds.has(meal.id)) {
        await axios.delete(`${API}/meals/saved/${meal.id}`);
        toast.success('Removed from saved meals');
      } else {
        await axios.post(`${API}/meals/saved/${meal.id}`);
        toast.success('Meal saved');
      }
      loadSaved();
    } catch (e) {
      toast.error('Failed to update saved meals');
    }
  };

  const handleOrder = async (meal, source) => {
    const url = meal.affiliate_url;
    if (!url) { toast.error('No order link available for this meal'); return; }
    axios.post(`${API}/meals/track-click`, {
      provider_id: meal.provider_id || meal.id,
      meal_id: meal.provider_id ? meal.id : null,
      source,
      mode: tab === 'today' ? 'today' : 'goal',
      platform: navigator.userAgent.includes('Mobi') ? 'mobile' : 'desktop',
    }).catch(() => {});
    toast.info(`Opening ${meal.provider_name || meal.name} — you're leaving Anvil & Ash`);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleVisitProvider = (provider) => {
    handleOrder({ ...provider, provider_id: null, affiliate_url: provider.affiliate_url || provider.website_url, provider_name: provider.name, id: provider.id }, 'provider_page');
  };

  const openLogForm = (meal) => {
    setLogForm({
      meal_id: meal.id, food_name: meal.name,
      calories: meal.calories, protein: meal.protein, carbs: meal.carbs, fats: meal.fats,
      servings: 1, date: getLocalDateString(),
    });
  };

  const submitLog = async () => {
    try {
      await axios.post(`${API}/meals/log`, {
        meal_id: logForm.meal_id, date: logForm.date, food_name: logForm.food_name,
        calories: parseFloat(logForm.calories), protein: parseFloat(logForm.protein),
        carbs: parseFloat(logForm.carbs), fats: parseFloat(logForm.fats),
        servings: parseFloat(logForm.servings) || 1,
      });
      toast.success('Added to your food log');
      setLogForm(null);
      setDetail(null);
    } catch (e) {
      toast.error('Failed to add to food log');
    }
  };

  const applyClientFilters = (meals) => {
    let out = [...(meals || [])];
    const f = filters;
    if (f.provider_id) out = out.filter(m => m.provider_id === f.provider_id);
    if (f.max_calories) out = out.filter(m => m.calories <= parseFloat(f.max_calories));
    if (f.min_protein) out = out.filter(m => m.protein >= parseFloat(f.min_protein));
    if (f.max_carbs) out = out.filter(m => m.carbs <= parseFloat(f.max_carbs));
    if (f.max_fats) out = out.filter(m => m.fats <= parseFloat(f.max_fats));
    if (f.max_price) out = out.filter(m => m.price == null || m.price <= parseFloat(f.max_price));
    if (f.dietary_tag) out = out.filter(m => (m.dietary_tags || []).includes(f.dietary_tag));
    if (f.category) out = out.filter(m => m.category === f.category);
    const sorters = {
      best: (a, b) => (b.match_score || 0) - (a.match_score || 0),
      protein: (a, b) => b.protein - a.protein,
      calories: (a, b) => a.calories - b.calories,
      price: (a, b) => (a.price ?? 9999) - (b.price ?? 9999),
      provider: (a, b) => (a.provider_name || '').localeCompare(b.provider_name || ''),
      newest: (a, b) => (b.created_at || '').localeCompare(a.created_at || ''),
    };
    out.sort(sorters[sort] || sorters.best);
    return out;
  };

  if (!isPremium) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-black">
        <HamburgerMenu />
        <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="prepared-meals-premium-gate">
          <Lock className="w-16 h-16 text-blood-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-blood-400 tracking-wider mb-2">PREPARED MEALS</h1>
          <p className="text-stone-400 mb-6">Meal delivery recommendations matched to your macros are a premium feature.</p>
          <Button onClick={() => navigate('/settings')} className="bg-gradient-to-r from-blood-700 to-blood-900 text-white font-bold px-8 py-3 rounded-xl tracking-wider">
            UPGRADE TO PREMIUM
          </Button>
        </div>
      </div>
    );
  }

  const displayMeals = tab === 'saved' ? applyClientFilters(savedMeals) : applyClientFilters(recs?.meals);

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-black overflow-x-hidden">
      <HamburgerMenu />
      <div className="max-w-7xl mx-auto px-4 py-4 animate-fade-in">
        <button onClick={() => navigate('/nutrition')} className="flex items-center gap-2 text-stone-400 hover:text-white mb-4 transition-colors" data-testid="back-to-nutrition-btn">
          <ArrowLeft className="w-4 h-4" /> Back to Nutrition
        </button>

        <div className="mb-4">
          <h1 className="text-2xl md:text-3xl font-bold text-blood-400 tracking-wider flex items-center gap-3">
            <UtensilsCrossed className="w-7 h-7" /> PREPARED MEALS
          </h1>
          <p className="text-stone-400 text-sm mt-1">
            Find prepared meals that fit your calories, protein goals, dietary preferences, and fitness objectives.
          </p>
          <p className="text-stone-600 text-xs mt-2 flex items-start gap-1" data-testid="affiliate-disclosure">
            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
            Anvil &amp; Ash may earn a commission when you purchase through certain provider links. This does not increase
            your purchase price and does not affect how meals are ranked.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          {[
            ['goal', 'For My Goal', Target],
            ['today', 'Fits Today', Flame],
            ['saved', `Saved (${savedMeals.length})`, Bookmark],
            ['providers', 'Providers', Store],
          ].map(([key, label, Icon]) => (
            <button key={key} onClick={() => switchTab(key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg border-2 text-sm font-semibold transition-all ${
                tab === key ? 'border-blood-600 bg-blood-950/30 text-blood-400' : 'border-stone-700 text-stone-400 hover:border-blood-800'
              }`}
              data-testid={`meals-tab-${key}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
          <div className="ml-auto flex gap-2">
            <button onClick={() => setShowPrefs(!showPrefs)} className={`px-3 py-2 rounded-lg border-2 text-sm ${showPrefs ? 'border-blood-600 text-blood-400' : 'border-stone-700 text-stone-400'}`} data-testid="meals-prefs-toggle">
              Preferences
            </button>
            <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-1 px-3 py-2 rounded-lg border-2 text-sm ${showFilters ? 'border-blood-600 text-blood-400' : 'border-stone-700 text-stone-400'}`} data-testid="meals-filters-toggle">
              <SlidersHorizontal className="w-4 h-4" /> Filters
            </button>
          </div>
        </div>

        {/* Preferences panel */}
        {showPrefs && (
          <Card className="glass p-4 border border-stone-800 mb-4" data-testid="meals-prefs-panel">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-stone-400 text-xs uppercase tracking-wider block mb-1">Dietary Preference</label>
                <select value={prefs.dietary_preference || 'none'}
                  onChange={(e) => savePrefs({ ...prefs, dietary_preference: e.target.value })}
                  className="w-full glass text-white px-3 py-2 rounded-lg border border-stone-700 text-sm"
                  data-testid="meals-diet-pref">
                  {DIET_PREFS.map(p => <option key={p} value={p} className="capitalize">{tagLabel(p)}</option>)}
                </select>
              </div>
              <div>
                <label className="text-stone-400 text-xs uppercase tracking-wider block mb-1">Meals Per Day</label>
                <select value={prefs.meals_per_day || 3}
                  onChange={(e) => savePrefs({ ...prefs, meals_per_day: parseInt(e.target.value) })}
                  className="w-full glass text-white px-3 py-2 rounded-lg border border-stone-700 text-sm"
                  data-testid="meals-per-day">
                  {[2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div className="md:col-span-3">
                <label className="text-stone-400 text-xs uppercase tracking-wider block mb-1">Food Allergies (meals containing these are excluded)</label>
                <div className="flex flex-wrap gap-2">
                  {ALLERGIES.map(a => (
                    <button key={a} onClick={() => toggleAllergy(a)}
                      className={`text-xs px-2.5 py-1 rounded-full border capitalize ${
                        prefs.allergies?.includes(a) ? 'border-blood-600 bg-blood-950/40 text-blood-400' : 'border-stone-700 text-stone-500'
                      }`}
                      data-testid={`meals-allergy-${a}`}>
                      {a}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Filters panel */}
        {showFilters && tab !== 'providers' && (
          <Card className="glass p-4 border border-stone-800 mb-4" data-testid="meals-filters-panel">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <select value={filters.provider_id} onChange={(e) => setFilters({ ...filters, provider_id: e.target.value })}
                className="glass text-white px-3 py-2 rounded-lg border border-stone-700 text-sm" data-testid="filter-provider">
                <option value="">All Providers</option>
                {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <Input type="number" placeholder="Max calories" value={filters.max_calories}
                onChange={(e) => setFilters({ ...filters, max_calories: e.target.value })}
                className="bg-stone-900 border-stone-700 text-white text-sm" data-testid="filter-max-calories" />
              <Input type="number" placeholder="Min protein (g)" value={filters.min_protein}
                onChange={(e) => setFilters({ ...filters, min_protein: e.target.value })}
                className="bg-stone-900 border-stone-700 text-white text-sm" data-testid="filter-min-protein" />
              <Input type="number" placeholder="Max carbs (g)" value={filters.max_carbs}
                onChange={(e) => setFilters({ ...filters, max_carbs: e.target.value })}
                className="bg-stone-900 border-stone-700 text-white text-sm" data-testid="filter-max-carbs" />
              <Input type="number" placeholder="Max fat (g)" value={filters.max_fats}
                onChange={(e) => setFilters({ ...filters, max_fats: e.target.value })}
                className="bg-stone-900 border-stone-700 text-white text-sm" data-testid="filter-max-fats" />
              <Input type="number" placeholder="Max price ($)" value={filters.max_price}
                onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
                className="bg-stone-900 border-stone-700 text-white text-sm" data-testid="filter-max-price" />
              <select value={filters.dietary_tag} onChange={(e) => setFilters({ ...filters, dietary_tag: e.target.value })}
                className="glass text-white px-3 py-2 rounded-lg border border-stone-700 text-sm" data-testid="filter-dietary">
                <option value="">Any Diet Tag</option>
                {['high_protein', 'low_calorie', 'low_carb', 'keto', 'vegetarian', 'vegan', 'gluten_free', 'dairy_free', 'pescatarian', 'balanced'].map(t =>
                  <option key={t} value={t}>{tagLabel(t)}</option>)}
              </select>
              <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                className="glass text-white px-3 py-2 rounded-lg border border-stone-700 text-sm" data-testid="filter-category">
                <option value="">Any Category</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{tagLabel(c)}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <span className="text-stone-500 text-xs uppercase tracking-wider">Sort:</span>
              <select value={sort} onChange={(e) => setSort(e.target.value)}
                className="glass text-white px-3 py-1.5 rounded-lg border border-stone-700 text-sm" data-testid="meals-sort">
                <option value="best">Best Match</option>
                <option value="protein">Highest Protein</option>
                <option value="calories">Lowest Calories</option>
                <option value="price">Lowest Price</option>
                <option value="provider">Provider</option>
                <option value="newest">Newest</option>
              </select>
              <button onClick={() => setFilters({ provider_id: '', max_calories: '', min_protein: '', max_carbs: '', max_fats: '', max_price: '', dietary_tag: '', category: '' })}
                className="text-stone-500 text-xs hover:text-white ml-auto" data-testid="filters-clear">
                Clear filters
              </button>
            </div>
          </Card>
        )}

        {/* Targets strip */}
        {(tab === 'goal' || tab === 'today') && recs && (
          <Card className="glass p-3 border border-stone-800 mb-4" data-testid="meals-targets-strip">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
              <span className="text-blood-400 font-bold uppercase tracking-wider text-xs">
                {tab === 'today' ? 'Remaining Today' : `Per-Meal Target (${recs.meals_per_day} meals/day)`}
              </span>
              {Object.entries(tab === 'today' && recs.remaining ? recs.remaining : recs.targets).map(([k, v]) => (
                <span key={k} className="text-stone-300"><span className="text-stone-500 capitalize">{k}:</span> <b>{v}{k === 'calories' ? '' : 'g'}</b></span>
              ))}
            </div>
            {recs.using_default_goals && (
              <p className="text-amber-400/90 text-xs mt-2" data-testid="meals-goals-prompt">
                You haven't set nutrition goals yet — recommendations use defaults.{' '}
                <button onClick={() => navigate('/nutrition')} className="underline hover:text-amber-300">Set your goals</button> for better matches.
              </p>
            )}
          </Card>
        )}

        {/* Content */}
        {loading ? (
          <div className="text-center py-16 text-blood-400 font-bold tracking-wider" data-testid="meals-loading">FINDING YOUR MEALS...</div>
        ) : error ? (
          <div className="text-center py-16 text-stone-400" data-testid="meals-error">{error}</div>
        ) : tab === 'providers' ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="providers-comparison">
            {providers.map(p => (
              <Card key={p.id} className="glass p-5 border border-stone-800 flex flex-col" data-testid={`provider-card-${p.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-white font-bold">{p.name}</h3>
                  {p.is_featured && <span className="text-[10px] bg-amber-900/60 text-amber-300 border border-amber-700 px-2 py-0.5 rounded-full font-bold">FEATURED PARTNER</span>}
                </div>
                <p className="text-stone-400 text-sm mb-3 flex-1">{p.description}</p>
                <div className="space-y-1.5 text-sm mb-3">
                  <p className="text-stone-400"><span className="text-stone-600">Avg meal price:</span> {p.avg_meal_price ? `$${p.avg_meal_price.toFixed(2)}` : '—'} <span className="text-stone-600">({p.price_range})</span></p>
                  <p className="text-stone-400"><span className="text-stone-600">Subscription:</span> {p.subscription_required ? 'Required' : 'Not required'}</p>
                  <p className="text-stone-400"><span className="text-stone-600">Delivery:</span> {p.delivery_regions || '—'}</p>
                  <p className="text-stone-400"><span className="text-stone-600">Minimum:</span> {p.min_order_info || 'None'}</p>
                  <p className="text-stone-400"><span className="text-stone-600">Meals in catalog:</span> {p.meal_count}</p>
                </div>
                {p.promo_text && (
                  <div className="bg-blood-950/30 border border-blood-900 rounded-lg p-2 mb-3">
                    <p className="text-blood-400 text-xs font-semibold flex items-center gap-1"><Tag className="w-3 h-3" /> {p.promo_text}{p.coupon_code ? ` — code ${p.coupon_code}` : ''}</p>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setFilters({ ...filters, provider_id: p.id }); switchTab('goal'); }}
                    className="flex-1 glass border-stone-700 text-stone-300 text-xs" data-testid={`provider-view-meals-${p.id}`}>
                    View Meals
                  </Button>
                  <Button size="sm" onClick={() => handleVisitProvider(p)}
                    className="flex-1 bg-gradient-to-r from-blood-700 to-blood-900 text-white text-xs font-bold" data-testid={`provider-visit-${p.id}`}>
                    Visit <ExternalLink className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </Card>
            ))}
            {providers.length === 0 && <p className="text-stone-500 col-span-3 text-center py-12">No providers available yet.</p>}
          </div>
        ) : displayMeals.length === 0 ? (
          <div className="text-center py-16" data-testid="meals-empty">
            <UtensilsCrossed className="w-12 h-12 text-stone-700 mx-auto mb-3" />
            <p className="text-stone-400">{tab === 'saved' ? 'No saved meals yet — tap the heart on any meal to save it.' : 'No meals match your preferences and filters right now.'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="meals-grid">
            {displayMeals.map(meal => (
              <MealCard key={meal.id} meal={meal}
                onDetails={setDetail} onOrder={handleOrder} onSave={handleSave}
                isSaved={savedIds.has(meal.id)} />
            ))}
          </div>
        )}
      </div>

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => { setDetail(null); setLogForm(null); }}>
          <Card className="glass border border-stone-700 max-w-lg w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="meal-detail-modal">
            <div className="relative">
              {detail.image_url && <img src={detail.image_url} alt={detail.name} className="w-full h-48 object-cover rounded-t-xl" />}
              <button onClick={() => { setDetail(null); setLogForm(null); }} className="absolute top-2 right-2 bg-stone-950/70 rounded-full p-2" data-testid="meal-detail-close">
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
            <div className="p-5">
              <p className="text-stone-500 text-xs uppercase tracking-wider">{detail.provider_name}</p>
              <div className="flex items-center justify-between gap-2 mb-1">
                <h2 className="text-xl font-bold text-white">{detail.name}</h2>
                {detail.match_label && <MatchBadge label={detail.match_label} />}
              </div>
              <p className="text-stone-400 text-sm mb-3">{detail.description}</p>
              <div className="grid grid-cols-3 gap-2 text-center mb-3">
                {[['Calories', detail.calories], ['Protein', `${detail.protein}g`], ['Carbs', `${detail.carbs}g`],
                  ['Fat', `${detail.fats}g`], ['Fiber', detail.fiber != null ? `${detail.fiber}g` : '—'], ['Sodium', detail.sodium != null ? `${detail.sodium}mg` : '—']].map(([k, v]) => (
                  <div key={k} className="bg-stone-900/70 rounded-lg py-2">
                    <p className="text-blood-400 font-bold">{v}</p>
                    <p className="text-stone-500 text-[10px] uppercase">{k}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-1 mb-2">
                {(detail.dietary_tags || []).map(t => <span key={t} className="text-[10px] bg-stone-800 text-stone-400 px-1.5 py-0.5 rounded capitalize">{tagLabel(t)}</span>)}
                {(detail.allergy_tags || []).map(t => <span key={t} className="text-[10px] bg-blood-950/50 text-blood-400 border border-blood-900 px-1.5 py-0.5 rounded capitalize">contains {t}</span>)}
              </div>
              <p className="text-stone-500 text-xs mb-1">Serving: {detail.serving_size || '—'} {detail.price != null && <>• Price: <b className="text-white">${detail.price.toFixed(2)}</b></>}</p>
              {detail.provider_coupon && <p className="text-amber-400 text-xs mb-2">Coupon code: <b>{detail.provider_coupon}</b></p>}
              {(detail.match_reasons || []).map((r, i) => <p key={i} className="text-stone-400 text-xs italic">• {r}</p>)}

              {logForm ? (
                <div className="mt-4 border-t border-stone-800 pt-4" data-testid="meal-log-form">
                  <p className="text-stone-300 text-sm font-semibold mb-2">Review before adding to your food log:</p>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div className="col-span-2">
                      <label className="text-stone-500 text-xs">Name</label>
                      <Input value={logForm.food_name} onChange={(e) => setLogForm({ ...logForm, food_name: e.target.value })} className="bg-stone-900 border-stone-700 text-white text-sm" data-testid="log-food-name" />
                    </div>
                    {[['calories', 'Calories'], ['protein', 'Protein (g)'], ['carbs', 'Carbs (g)'], ['fats', 'Fat (g)']].map(([k, lbl]) => (
                      <div key={k}>
                        <label className="text-stone-500 text-xs">{lbl}</label>
                        <Input type="number" value={logForm[k]} onChange={(e) => setLogForm({ ...logForm, [k]: e.target.value })} className="bg-stone-900 border-stone-700 text-white text-sm" data-testid={`log-${k}`} />
                      </div>
                    ))}
                    <div>
                      <label className="text-stone-500 text-xs">Servings</label>
                      <Input type="number" step="0.5" value={logForm.servings} onChange={(e) => setLogForm({ ...logForm, servings: e.target.value })} className="bg-stone-900 border-stone-700 text-white text-sm" data-testid="log-servings" />
                    </div>
                    <div>
                      <label className="text-stone-500 text-xs">Date</label>
                      <Input type="date" value={logForm.date} onChange={(e) => setLogForm({ ...logForm, date: e.target.value })} className="bg-stone-900 border-stone-700 text-white text-sm" data-testid="log-date" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setLogForm(null)} className="glass border-stone-700 text-stone-400 text-xs">Cancel</Button>
                    <Button size="sm" onClick={submitLog} className="flex-1 bg-gradient-to-r from-blood-700 to-blood-900 text-white text-xs font-bold" data-testid="log-submit-btn">Save to Food Log</Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" onClick={() => handleSave(detail)} className="glass border-stone-700 text-stone-300 text-xs" data-testid="detail-save-btn">
                    <Heart className={`w-3.5 h-3.5 mr-1 ${savedIds.has(detail.id) ? 'fill-blood-500 text-blood-500' : ''}`} />
                    {savedIds.has(detail.id) ? 'Saved' : 'Save'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openLogForm(detail)} className="flex-1 glass border-stone-700 text-stone-300 text-xs" data-testid="detail-add-to-log-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add to Food Log
                  </Button>
                  <Button size="sm" onClick={() => handleOrder(detail, 'detail')} className="flex-1 bg-gradient-to-r from-blood-700 to-blood-900 text-white text-xs font-bold" data-testid="detail-order-btn">
                    Order Meal <ExternalLink className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              )}
              <p className="text-stone-600 text-[10px] mt-3">Ordering opens the provider's site in a new tab. Ordering does not log the meal — use "Add to Food Log" when you eat it.</p>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
